import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, CheckCircle, XCircle, AlertCircle, RefreshCw,
  ChevronLeft, ChevronRight, FileText, Clock, ChevronDown, Download
} from "lucide-react";

const API = (process.env.REACT_APP_API_URL || "https://api-vietnam.finkraftai.com") + "/api/gst";
const PAGE_SIZE = 100;

// ── Card → API filter mapping ─────────────────────────────────────────────────
// Each card ID maps to the exact query params sent to the backend
const CARD_FILTERS = {
  total:     {},
  clean:     { has_error: "false" },
  errors:    { has_error: "true"  },
  b2b:       { customer_type: "B2B" },
  b2b_clean: { customer_type: "B2B", has_error: "false" },
  b2b_error: { customer_type: "B2B", has_error: "true"  },
  b2c:       { customer_type: "B2C" },
  b2c_clean: { customer_type: "B2C", has_error: "false" },
  b2c_error: { customer_type: "B2C", has_error: "true"  },
};

function applyFilters(params, card) {
  const f = CARD_FILTERS[card] || {};
  if (f.has_error     !== undefined) params.set("has_error",     f.has_error);
  if (f.customer_type !== undefined) params.set("customer_type", f.customer_type);
}

function runChecks(row) {
  const p2    = (row.pos||"").slice(0,2), v2=(row.vna_gstin||"").slice(0,2);
  const gstId = (row.gst_id||"").trim();
  const isB2C = !gstId||["B2C","nan",""].includes(gstId);
  const c2    = isB2C?"":gstId.slice(0,2);
  const cgst  = parseFloat(row.cgst)||0;
  const RS    = {DEL:"07",AMD:"24",BLR:"29",BOM:"27",MAA:"33",HYD:"36",CCU:"19",COK:"32",GOI:"30",GAU:"18",JAI:"08",LKO:"09"};
  const errors=[];
  if (!isB2C) {
    if (p2!==v2) errors.push({label:"POS vs VNA",msg:`POS(${p2})≠VNA(${v2})`});
    if (!(p2===v2&&p2===c2&&cgst>0)) {
      const w=[];
      if(p2!==v2)w.push(`POS≠VNA`); if(p2!==c2)w.push(`POS≠Cust`); if(cgst<=0)w.push("CGST=0");
      errors.push({label:"Intrastate",msg:w.join("; ")});
    }
    const exp=RS[(row.route||"").slice(0,3).toUpperCase()];
    if(exp&&p2!==exp)errors.push({label:"Sector",msg:`exp ${exp}, got ${p2}`});
  }
  return {errors,hasError:errors.length>0};
}

// Small reusable stat pill
function StatPill({label, value, color, card, activeCard, onClick}) {
  return (
    <button onClick={()=>onClick(card)}
      className={`flex flex-col items-start p-2 rounded-lg transition-colors w-full ${activeCard===card?"ring-2 ring-[#367588] bg-white":"hover:bg-white/60"}`}>
      <span className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-lg font-bold ${color}`}>{(value||0).toLocaleString()}</span>
    </button>
  );
}

export default function ValidationPage() {
  const navigate = useNavigate();
  const [allSessions,  setAllSessions]  = useState([]);
  const [selectedSid,  setSelectedSid]  = useState(null);
  const [showDrop,     setShowDrop]     = useState(false);
  const dropRef = useRef(null);

  const [uploading,  setUploading]  = useState(false);
  const [uploadErr,  setUploadErr]  = useState("");
  const [uploadDone, setUploadDone] = useState(false);
  const fileRef = useRef(null);

  const [stats,        setStats]        = useState(null);
  const [activeCard,   setActiveCard]   = useState(null);
  const [rows,         setRows]         = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [expanded,     setExpanded]     = useState(new Set());
  const [dlCard,       setDlCard]       = useState(null);
  const [modal,        setModal]        = useState(null);
  const [pdfGen,       setPdfGen]       = useState(null);
  const totalPages = Math.ceil(total/PAGE_SIZE);

  useEffect(()=>{
    const h=e=>{if(dropRef.current&&!dropRef.current.contains(e.target))setShowDrop(false);};
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[]);

  useEffect(()=>{
    (async()=>{
      try {
        const res=await fetch(`${API}/sessions?page_size=50`);
        if(res.ok){const items=(await res.json()).items||[];setAllSessions(items);if(items.length>0)setUploadDone(true);}
        await loadStats(null);
      }catch{}
    })();
  },[]); // eslint-disable-line

  const loadStats=async(sid)=>{
    try{const res=await fetch(sid?`${API}/stats?session_id=${sid}`:`${API}/stats`);if(res.ok)setStats(await res.json());}catch{}
  };

  const pollPdfStatus=async(sid)=>{
    try{
      const res=await fetch(`${API}/generation-status/${sid}`);
      if(!res.ok)return;
      const s=await res.json();
      if(s.status==="running"){
        setPdfGen({done:s.done||0,total:s.total||0});
        setTimeout(()=>pollPdfStatus(sid),3000);
      } else {
        setPdfGen(null); // generation done — hide banner
      }
    }catch{}
  };

  const handleFileFilter=async(sid)=>{
    setSelectedSid(sid);setShowDrop(false);setActiveCard(null);setRows([]);setPage(1);await loadStats(sid);
  };

  const fetchRows=useCallback(async(sid,card,pg)=>{
    setTableLoading(true);
    try{
      const p=new URLSearchParams({page:pg,page_size:PAGE_SIZE});
      if(sid)p.set("session_id",sid);
      applyFilters(p, card);
      const res=await fetch(`${API}/rows?${p}`);
      const data=await res.json();
      setRows((data.items||[]).map(r=>({...r,...runChecks(r)})));
      setTotal(data.total||0);
    }catch{}finally{setTableLoading(false);}
  },[]);

  const handleCardClick=(card)=>{setActiveCard(card);setPage(1);setExpanded(new Set());fetchRows(selectedSid,card,1);};
  const handlePage=(pg)=>{setPage(pg);fetchRows(selectedSid,activeCard,pg);};
  const toggleExp=(id)=>setExpanded(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});

  const handleUpload=useCallback(async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    if(!/\.(xlsx|xls|csv)$/i.test(file.name)){setUploadErr("Please upload .xlsx, .xls or .csv");return;}
    setUploading(true);setUploadErr("");
    try{
      const form=new FormData();form.append("file",file);
      const res=await fetch(`${API}/upload`,{method:"POST",body:form});
      if(!res.ok){const e=await res.json().catch(()=>{});throw new Error(e?.detail||`Error ${res.status}`);}
      const data=await res.json();
      const sr=await fetch(`${API}/sessions?page_size=50`);
      if(sr.ok)setAllSessions((await sr.json()).items||[]);
      setSelectedSid(data.session_id);await loadStats(data.session_id);
      setUploadDone(true);setActiveCard(null);setRows([]);setPage(1);
      setPdfGen({done:0,total:data.total_rows||0});
      setTimeout(()=>pollPdfStatus(data.session_id),3000);
    }catch(err){setUploadErr(`Upload failed: ${err.message}`);}
    finally{setUploading(false);if(fileRef.current)fileRef.current.value="";}
  },[]);

  const downloadExcel=async(card)=>{
    setDlCard(card);
    try{
      const p=new URLSearchParams();
      if(selectedSid)p.set("session_id",selectedSid);
      applyFilters(p, card);
      const res=await fetch(`${API}/export?${p}`);
      if(!res.ok)throw new Error(`${res.status}`);
      const blob=await res.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      const cd=res.headers.get("Content-Disposition")||"";
      a.href=url;a.download=cd.match(/filename="([^"]+)"/)?.[1]||`GST_${card}.xlsx`;
      document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
    }catch(err){setModal({title:"Download Failed", message:"Download failed: "+err.message});}
    finally{setDlCard(null);}
  };

  const goToInvoices=(card)=>{
    const sid=selectedSid||(allSessions.length===1?allSessions[0].session_id:null);
    if(!sid){setModal({title:"Select a File", message:"Please select a specific file from the dropdown first."});return;}
    const p=new URLSearchParams({source:"validation",session_id:sid,card});
    navigate(`/admin/invoices?${p}`);
  };

  const s=stats||{};
  const COLS=["#","Ticket #","PAX Name","Date","POS","VNA GSTIN","Route","GST-ID","Class","Rate","Base ₹","SGST","CGST","IGST","GST Liability","Total ₹","Invoice #","Status","Errors"];
  const selSess=allSessions.find(s=>s.session_id===selectedSid);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">GST Data Validation</h1>
          <p className="text-sm text-gray-500 mt-1">Upload filing data → validate → generate invoices</p>
        </div>
        <div className="flex items-center gap-3">
          {allSessions.length>0&&(
            <div className="relative" ref={dropRef}>
              <button onClick={()=>setShowDrop(p=>!p)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 min-w-[200px] justify-between">
                <div className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-gray-400 flex-shrink-0"/><span className="truncate max-w-[180px]">{selSess?selSess.filename:"All Files"}</span></div>
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0"/>
              </button>
              {showDrop&&(
                <div className="absolute right-0 top-full mt-1 w-[380px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <button onClick={()=>handleFileFilter(null)} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-100 ${!selectedSid?"bg-blue-50":""}`}>
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"><FileText className="w-4 h-4 text-gray-500"/></div>
                    <div className="flex-1"><p className="text-sm font-semibold text-gray-900">All Files</p><p className="text-xs text-gray-500">{allSessions.length} files · {allSessions.reduce((s,x)=>s+(x.stats?.total_rows||0),0).toLocaleString()} rows</p></div>
                    {!selectedSid&&<CheckCircle className="w-4 h-4 text-blue-600"/>}
                  </button>
                  <div className="max-h-64 overflow-y-auto">
                    {allSessions.map(sess=>(
                      <button key={sess.session_id} onClick={()=>handleFileFilter(sess.session_id)} className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 text-left border-b border-gray-50 ${selectedSid===sess.session_id?"bg-blue-50":""}`}>
                        <div className="w-8 h-8 rounded-lg bg-[#367588]/10 flex items-center justify-center mt-0.5"><FileText className="w-4 h-4 text-[#367588]"/></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{sess.filename}</p>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(sess.uploaded_at).toLocaleString()}</span>
                            <span className="text-green-700">{(sess.stats?.total_rows||0).toLocaleString()} rows</span>
                            <span className="text-red-600">{sess.stats?.error_rows||0} errors</span>
                          </div>
                        </div>
                        {selectedSid===sess.session_id&&<CheckCircle className="w-4 h-4 text-blue-600 mt-1"/>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <label className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${uploading?"bg-gray-200 text-gray-400":"bg-[#367588] text-white hover:bg-[#2a5a6b]"}`}>
            <Upload className="w-4 h-4"/>{uploading?"Uploading…":"Upload GST Excel / CSV"}
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleUpload} disabled={uploading}/>
          </label>
        </div>
      </div>

      {uploading&&<div className="mb-5 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-800"><RefreshCw className="w-5 h-5 animate-spin"/><div><p className="text-sm font-semibold">Uploading and validating…</p><p className="text-xs opacity-70">May take 30–60s for large files</p></div></div>}
      {uploadErr&&<div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-800"><AlertCircle className="w-5 h-5"/><p className="text-sm font-semibold flex-1">{uploadErr}</p><button onClick={()=>setUploadErr("")} className="text-red-400 text-xl">×</button></div>}

      {!uploadDone&&!uploading&&(
        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer">
          <Upload className="w-12 h-12 text-gray-400 mb-3"/>
          <p className="text-base font-semibold text-gray-700">Drop GST Filing Excel here or click to browse</p>
          <p className="text-sm text-gray-400 mt-1">Supports .xlsx · .xls · .csv</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleUpload} disabled={uploading}/>
        </label>
      )}

      {uploadDone&&stats&&(<>
        {/* Info bar */}
        <div className="mb-5 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3 flex-wrap">
          <FileText className="w-4 h-4 text-gray-500"/>
          <span className="text-sm font-semibold text-gray-900">{selSess?selSess.filename:`All Files (${allSessions.length} uploads)`}</span>
          {selSess&&<span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(selSess.uploaded_at).toLocaleString()}</span>}
          <span className="text-xs text-gray-500 ml-auto">{(s.total_rows||0).toLocaleString()} rows · {(s.clean_rows||0).toLocaleString()} clean · {s.error_rows||0} errors</span>
        </div>

        {pdfGen && (
          <div className="mb-4 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
            <RefreshCw className="w-4 h-4 animate-spin flex-shrink-0"/>
            <span>Generating invoices… {pdfGen.done.toLocaleString()} / {pdfGen.total.toLocaleString()} — Invoice Generator will show ready invoices as they complete</span>
          </div>
        )}

        {/* Row 1: Total / Clean / Errors — simple cards */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            {id:"total", label:"Total Rows",  value:s.total_rows, color:"text-gray-900",  bg:"bg-white",    border:"border-gray-200"},
            {id:"clean", label:"Clean",        value:s.clean_rows, color:"text-green-700", bg:"bg-green-50", border:"border-green-200"},
            {id:"errors",label:"Has Errors",   value:s.error_rows, color:"text-red-700",   bg:"bg-red-50",   border:"border-red-200"},
          ].map(card=>(
            <div key={card.id} className={`${card.bg} rounded-xl border-2 p-4 transition-all ${activeCard===card.id?`${card.border} ring-2 ring-offset-1`:card.border}`}>
              <button className="w-full text-left" onClick={()=>handleCardClick(card.id)}>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{card.label}</p>
                <p className={`text-3xl font-bold ${card.color}`}>{(card.value||0).toLocaleString()}</p>
              </button>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <button onClick={()=>downloadExcel(card.id)} disabled={dlCard===card.id}
                  className="flex items-center gap-1 px-2 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50 flex-1 justify-center">
                  {dlCard===card.id?<RefreshCw className="w-3.5 h-3.5 animate-spin"/>:<Download className="w-3.5 h-3.5"/>}Excel
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Row 2: B2B and B2C — expanded with sub-breakdown */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {[
            {id:"b2b", label:"B2B Transactions", total:s.b2b_rows, clean:s.b2b_clean, error:s.b2b_error, color:"text-blue-700",   bg:"bg-blue-50",   border:"border-blue-200"},
            {id:"b2c", label:"B2C Transactions", total:s.b2c_rows, clean:s.b2c_clean, error:s.b2c_error, color:"text-purple-700", bg:"bg-purple-50", border:"border-purple-200"},
          ].map(card=>(
            <div key={card.id} className={`${card.bg} rounded-xl border-2 p-4 ${activeCard===card.id||activeCard===`${card.id}_clean`||activeCard===`${card.id}_error`?`${card.border} ring-2 ring-offset-1`:card.border}`}>
              {/* Header */}
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{card.label}</p>
              <p className={`text-3xl font-bold ${card.color} mb-3`}>{(card.total||0).toLocaleString()}</p>

              {/* Sub-breakdown: Total / Clean / Error */}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-current border-opacity-20 mb-3">
                <StatPill label="Total"    value={card.total} color={card.color}        card={card.id}              activeCard={activeCard} onClick={handleCardClick}/>
                <StatPill label="Clean"    value={card.clean} color="text-green-700"    card={`${card.id}_clean`}   activeCard={activeCard} onClick={handleCardClick}/>
                <StatPill label="Errors"   value={card.error} color="text-red-600"      card={`${card.id}_error`}   activeCard={activeCard} onClick={handleCardClick}/>
              </div>

              {/* Action buttons for whichever sub-card is active */}
              {[card.id, `${card.id}_clean`, `${card.id}_error`].includes(activeCard) && (
                <div className="flex items-center gap-2 pt-3 border-t border-current border-opacity-20">
                  <button onClick={()=>downloadExcel(activeCard)} disabled={dlCard===activeCard}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50 flex-1 justify-center">
                    {dlCard===activeCard?<RefreshCw className="w-3.5 h-3.5 animate-spin"/>:<Download className="w-3.5 h-3.5"/>}Download Excel
                  </button>
      
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Table */}
        {activeCard&&(
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {({total:"All Rows",clean:"Clean Rows",errors:"Error Rows",b2b:"B2B — All",b2b_clean:"B2B — Clean",b2b_error:"B2B — Errors",b2c:"B2C — All",b2c_clean:"B2C — Clean",b2c_error:"B2C — Errors"})[activeCard]}
                <span className="text-gray-400 font-normal ml-2">({total.toLocaleString()} rows)</span>
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">{selSess?`File: ${selSess.filename}`:"All files"} · Page {page}/{totalPages||1} · click row to expand</p>
            </div>
            {tableLoading?(
              <div className="flex items-center justify-center py-16 gap-3 text-gray-400"><RefreshCw className="w-6 h-6 animate-spin text-[#367588]"/><span>Loading…</span></div>
            ):(
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200"><tr>{COLS.map(h=><th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.length===0?<tr><td colSpan={COLS.length} className="px-6 py-12 text-center text-gray-400">No rows found</td></tr>
                    :rows.map((row,ri)=>{const isExp=expanded.has(row._id);return(<>
                      <tr key={row._id} className={`cursor-pointer ${row.hasError?"bg-red-50/40 hover:bg-red-50":"hover:bg-gray-50"}`} onClick={()=>toggleExp(row._id)}>
                        <td className="px-3 py-2.5 text-xs text-gray-400">{(page-1)*PAGE_SIZE+ri+1}</td>
                        <td className="px-3 py-2.5 text-xs font-mono text-gray-800 whitespace-nowrap">{row.tkt_number}</td>
                        <td className="px-3 py-2.5 text-sm font-medium text-gray-900 max-w-[140px] truncate">{row.pax_name}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{String(row.fr_date||"").slice(0,10)}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{row.pos}</td>
                        <td className="px-3 py-2.5 text-xs font-mono text-gray-700 whitespace-nowrap">{row.vna_gstin||"—"}</td>
                        <td className="px-3 py-2.5 text-xs font-mono">{row.route}</td>
                        <td className="px-3 py-2.5 text-xs font-mono"><span className={`px-1.5 py-0.5 rounded text-xs ${!row.gst_id||row.gst_id==="B2C"?"bg-gray-100 text-gray-600":"bg-blue-50 text-blue-700"}`}>{row.gst_id||"B2C"}</span></td>
                        <td className="px-3 py-2.5 text-xs">{row.travel_class||"—"}</td>
                        <td className="px-3 py-2.5 text-xs font-medium text-purple-700">{Math.round((row.gst_rate||0)*100)}%</td>
                        <td className="px-3 py-2.5 text-xs text-right">{(row.base_charge||0).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2.5 text-xs text-right">{row.sgst>0?row.sgst.toLocaleString("en-IN",{maximumFractionDigits:1}):"—"}</td>
                        <td className="px-3 py-2.5 text-xs text-right">{row.cgst>0?row.cgst.toLocaleString("en-IN",{maximumFractionDigits:1}):"—"}</td>
                        <td className="px-3 py-2.5 text-xs text-right">{row.igst>0?row.igst.toLocaleString("en-IN",{maximumFractionDigits:1}):"—"}</td>
                        <td className="px-3 py-2.5 text-xs text-right font-semibold text-[#367588]">{(row.gst_liability||0).toLocaleString("en-IN",{maximumFractionDigits:1})}</td>
                        <td className="px-3 py-2.5 text-xs text-right font-bold">{(row.total_collection||0).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2.5 text-xs font-mono text-blue-700 whitespace-nowrap">{row.gst_invoice||"—"}</td>
                        <td className="px-3 py-2.5">{row.hasError?<span className="flex items-center gap-1 text-xs text-red-700"><XCircle className="w-3.5 h-3.5"/>{row.errors.length} err</span>:<span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5"/>Clean</span>}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-400">{row.hasError?"↓":""}</td>
                      </tr>
                      {isExp&&row.hasError&&(<tr key={row._id+"x"} className="bg-red-50"><td colSpan={COLS.length} className="px-6 py-3"><div className="flex flex-wrap gap-2">{row.errors.map((err,i)=>(<div key={i} className="flex items-start gap-1.5 bg-white border border-red-200 rounded-lg px-3 py-2 text-xs"><AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5"/><span><strong className="text-red-800">{err.label}:</strong> <span className="text-red-700">{err.msg}</span></span></div>))}</div></td></tr>)}
                    </>);})}
                  </tbody>
                </table>
              </div>
            )}
            {totalPages>1&&!tableLoading&&(
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <button onClick={()=>handlePage(1)} disabled={page<=1} className="px-2 py-1 text-xs border rounded disabled:opacity-30 hover:bg-white">«</button>
                  <button onClick={()=>handlePage(page-1)} disabled={page<=1} className="px-3 py-1 text-xs border rounded disabled:opacity-30 hover:bg-white flex items-center gap-1"><ChevronLeft className="w-3 h-3"/>Prev</button>
                  <span className="text-xs text-gray-600 font-medium px-2">Page {page} / {totalPages}</span>
                  <button onClick={()=>handlePage(page+1)} disabled={page>=totalPages} className="px-3 py-1 text-xs border rounded disabled:opacity-30 hover:bg-white flex items-center gap-1">Next<ChevronRight className="w-3 h-3"/></button>
                  <button onClick={()=>handlePage(totalPages)} disabled={page>=totalPages} className="px-2 py-1 text-xs border rounded disabled:opacity-30 hover:bg-white">»</button>
                </div>
                <span className="text-xs text-gray-500">Rows {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE,total)} of {total.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </>)}

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={()=>setModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={e=>e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-red-600"/>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{modal.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{modal.message}</p>
              </div>
            </div>
            <button onClick={()=>setModal(null)}
              className="w-full py-2.5 bg-[#367588] text-white rounded-xl text-sm font-semibold hover:bg-[#2a5a6b]">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}